import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Upload,
  Steps,
  Button,
  Space,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Option } = Select;

const DoctorFormModal = ({
  open,
  onCancel,
  onSave,
  loading,
  editingDoctor,
  specialityData,
  branchData,
  uploadLoading,
  handleUpload,
  profileImageUrl,
  signatureUrl,
  setProfileImageUrl,
  setSignatureUrl,
}) => {
  const [form] = Form.useForm();
  const [step, setStep] = useState(0);

  const stepFields = [
    ["firstName", "lastName", "email", "contactInfo", "userName", "password"],
    ["profileImageUrl", "signature"],
    [
      "workplaceHospital",
      "experience",
      "licenseNo",
      "qualification",
      "rating",
      "dob",
    ],
    ["specialtyIds", "branchIds", "address", "description"],
  ];

  useEffect(() => {
    if (editingDoctor) {
      form.setFieldsValue({
        ...editingDoctor,
        dob: editingDoctor.dob ? dayjs(editingDoctor.dob) : null,
        specialtyIds: editingDoctor.specialties?.map((s) => s.id) || [],
        branchIds: editingDoctor.branches?.map((b) => b.id) || [],
        profileImageUrl: editingDoctor.profileImageUrl
          ? [
              {
                uid: "-1",
                name: "profile.jpeg",
                status: "done",
                url: editingDoctor.profileImageUrl,
              },
            ]
          : [],
        signature: editingDoctor.signature
          ? [
              {
                uid: "-2",
                name: "signature.jpeg",
                status: "done",
                url: editingDoctor.signature,
              },
            ]
          : [],
      });
      setProfileImageUrl(editingDoctor.profileImageUrl || null);
      setSignatureUrl(editingDoctor.signature || null);
    } else {
      form.resetFields();
      setProfileImageUrl(null);
      setSignatureUrl(null);
    }
  }, [editingDoctor, form, setProfileImageUrl, setSignatureUrl]);

  const nextStep = async () => {
    try {
      await form.validateFields(stepFields[step]);
      setStep((s) => Math.min(s + 1, 3));
    } catch {
      /* validation errors */
    }
  };

  const prevStep = () => setStep((s) => Math.max(s - 1, 0));
  const handleSave = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue(true);

      const payload = {
        ...values,
        id: editingDoctor?.id,
        dob: values.dob ? values.dob.format("YYYY-MM-DD") : undefined,
        profileImageUrl: profileImageUrl || values.profileImageUrl?.[0]?.url,
        signature: signatureUrl || values.signature?.[0]?.url,
      };

      onSave(payload);
    } catch (err) {
      console.error("Validation error:", err);
      message.error("Please fix errors before saving");
    }
  };

  return (
    <Modal
      width={920}
      title={editingDoctor ? "Edit Doctor" : "Create New Doctor"}
      open={open}
      onCancel={onCancel}
      confirmLoading={loading}
      // loading={loading}
      style={{ top: 20 }}
      footer={
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          {step > 0 && <Button onClick={prevStep}>Back</Button>}
          {step < 3 ? (
            <Button type="primary" onClick={nextStep}>
              Next
            </Button>
          ) : (
            <Button type="primary" onClick={handleSave} loading={loading}>
              {editingDoctor ? "Save Changes" : "Create"}
            </Button>
          )}
        </Space>
      }
    >
      <Form layout="vertical" form={form}>
        <Steps
          current={step}
          onChange={(newStep) => setStep(newStep)}
          items={[
            { title: "Basic Info" },
            { title: "Media Upload" },
            { title: "Professional Info" },
            { title: "Specialties & Address" },
          ]}
          style={{ marginBottom: 16 }}
        />

        {/* Step 0 */}
        {step === 0 && (
          <div className="grid grid-cols-2 gap-3">
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[
                { required: true, message: "First name is required" },
                {
                  pattern: /^[A-Z][a-zA-Z\s]*$/,
                  message: "First letter must be capital, only letters allowed",
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[
                { required: true, message: "Last name is required" },
                {
                  pattern: /^[A-Z][a-zA-Z\s]*$/,
                  message: "First letter must be capital, only letters allowed",
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Email is required" },
                { type: "email", message: "Enter a valid email address" },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="contactInfo"
              label="Contact Number"
              rules={[
                { required: true, message: "Contact number is required" },
                {
                  pattern: /^[0-9]{10,15}$/,
                  message: "Enter a valid contact number (10-15 digits)",
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="userName"
              label="Username"
              rules={[
                { required: true, message: "Username is required" },
                {
                  pattern: /^[a-zA-Z0-9_]{4,20}$/,
                  message: "4-20 chars, letters, numbers, underscores only",
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: !editingDoctor, message: "Password is required" },
                {
                  pattern:
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                  message:
                    "Min 8 chars, include uppercase, lowercase, number & special char",
                },
              ]}
            >
              <Input.Password />
            </Form.Item>
          </div>
        )}

        {/* Step 1 */}
        {step === 1 && (
          <div className="grid grid-cols-2 gap-3">
            <Form.Item
              name="profileImageUrl"
              label="Profile Image"
              valuePropName="fileList"
              getValueFromEvent={(e) => e && e.fileList}
              rules={[{ required: true, message: "Profile image is required" }]}
            >
              <Upload
                beforeUpload={() => false}
                listType="picture-card"
                maxCount={1}
                onChange={(info) => handleUpload(info, "profile")}
              >
                {uploadLoading ? (
                  "Uploading..."
                ) : (
                  <>
                    <UploadOutlined /> Upload
                  </>
                )}
              </Upload>
            </Form.Item>
            <Form.Item
              name="signature"
              label="Signature"
              valuePropName="fileList"
              getValueFromEvent={(e) => e && e.fileList}
              rules={[{ required: true, message: "Signature is required" }]}
            >
              <Upload
                beforeUpload={() => false}
                listType="picture-card"
                maxCount={1}
                onChange={(info) => handleUpload(info, "signature")}
              >
                {uploadLoading ? (
                  "Uploading..."
                ) : (
                  <>
                    <UploadOutlined /> Upload
                  </>
                )}
              </Upload>
            </Form.Item>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="grid grid-cols-2 gap-3">
            <Form.Item
              name="workplaceHospital"
              label="Workplace/Hospital"
              rules={[
                { required: true, message: "Workplace is required" },
                {
                  pattern: /^[A-Za-z0-9\s]+$/,
                  message:
                    "Special characters not allowed.",
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="experience"
              label="Experience (Years)"
              rules={[{ required: true, message: "Experience is required" }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              name="licenseNo"
              label="License Number"
              rules={[
                { required: true, message: "License number is required" },
                {
                  pattern: /^[A-Z]{3}-\d{4}$/,
                  message: "Format: ABC-1234 ",
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="qualification"
              label="Qualification"
              rules={[
                { required: true, message: "Qualification is required" },
                {
                  pattern: /^[A-Za-z0-9,\s]+$/,
                  message:
                    "Special characters not allowed.",
                },
              ]}
            >
              <Input placeholder="e.g. MBBS, MD, DM" />
            </Form.Item>

            <Form.Item
              name="rating"
              label="Rating"
              rules={[{ required: true, message: "Rating is required" }]}
            >
              <InputNumber min={1} max={5} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              name="dob"
              label="Date of Birth"
              rules={[
                { required: true, message: "Date of birth is required" },
                () => ({
                  validator(_, value) {
                    if (!value || value.isBefore(dayjs())) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Date of birth must be in the past")
                    );
                  },
                }),
              ]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <>
            <Form.Item
              name="specialtyIds"
              label="Specialties"
              rules={[
                { required: true, message: "Select at least one specialty" },
              ]}
            >
              <Select mode="multiple" placeholder="Select Specialties">
                {specialityData?.data?.map((s) => (
                  <Option key={s.specialityId} value={s.specialityId}>
                    {s.specialityName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="branchIds"
              label="Branches"
              rules={[
                { required: true, message: "Select at least one branch" },
              ]}
            >
              <Select mode="multiple" placeholder="Select Branches">
                {branchData?.data?.map((b) => (
                  <Option key={b.branchId} value={b.branchId}>
                    {b.branchName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="address" label="Address">
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={4} />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default DoctorFormModal;
